import { Pagination } from '@core/components/Pagination';
import { Button, SearchInput } from '@soteria/design-system-ve';
import type { ReactNode } from 'react';
import { useState, useMemo } from 'react';

import * as styles from './BulkSelector.css';

export interface BulkSelectableItem {
  id: string;
  [key: string]: unknown;
}

export interface BulkSelectorProps<T extends BulkSelectableItem> {
  items: T[];
  selectedItemIds: string[];
  onSelectedItemsChange: (selectedIds: string[]) => void;
  itemsPerPage?: number;
  searchPlaceholder?: string;
  searchAriaLabel?: string;
  emptySearchMessage?: string;
  emptyPageMessage?: string;
  legend: string;
  renderItem: (item: T) => ReactNode;
  getItemSearchText: (item: T) => string;
  className?: string;
  /** Optional custom actions to render inline with pagination */
  footerActions?: ReactNode;
}

export function BulkSelector<T extends BulkSelectableItem>({
  items,
  selectedItemIds,
  onSelectedItemsChange,
  itemsPerPage = 8,
  searchPlaceholder = 'Search...',
  searchAriaLabel = 'Search items',
  emptySearchMessage = 'No items found matching your search',
  emptyPageMessage = 'No items available on this page',
  legend,
  renderItem,
  getItemSearchText,
  className,
  footerActions,
}: BulkSelectorProps<T>) {
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Filter items based on search term
  const filteredItems = useMemo(() => {
    if (!searchTerm.trim()) {
      return items;
    }

    return items.filter(item => getItemSearchText(item).toLowerCase().includes(searchTerm.toLowerCase().trim()));
  }, [items, searchTerm, getItemSearchText]);

  // Pagination logic
  const { paginatedItems, totalPages, startIndex, endIndex } = useMemo(() => {
    const totalPages = Math.ceil(filteredItems.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = Math.min(startIndex + itemsPerPage, filteredItems.length);
    const paginatedItems = filteredItems.slice(startIndex, endIndex);

    return { paginatedItems, totalPages, startIndex, endIndex };
  }, [filteredItems, currentPage, itemsPerPage]);

  // Selection handlers
  const handleItemToggle = (itemId: string) => {
    const newSelection = selectedItemIds.includes(itemId) ? selectedItemIds.filter(id => id !== itemId) : [...selectedItemIds, itemId];

    onSelectedItemsChange(newSelection);
  };

  const handleSelectAllPage = () => {
    const newIds = paginatedItems.map(item => item.id);
    const uniqueIds = [...new Set([...selectedItemIds, ...newIds])];

    onSelectedItemsChange(uniqueIds);
  };

  const handleDeselectCurrentPage = () => {
    const currentPageIds = paginatedItems.map(item => item.id);

    onSelectedItemsChange(selectedItemIds.filter(id => !currentPageIds.includes(id)));
  };

  const handleSelectAll = () => {
    onSelectedItemsChange(filteredItems.map(item => item.id));
  };

  const handleSelectNone = () => {
    onSelectedItemsChange([]);
  };

  // Search handlers
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1); // Reset to first page when searching
  };

  const handleSearchClear = () => {
    setSearchTerm('');
    setCurrentPage(1);
  };

  return (
    <div className={className}>
      <div className={styles.formField}>
        <div aria-labelledby="bulk-selection-legend" className={styles.fieldLabel} role="group">
          <span id="bulk-selection-legend">{legend}</span>
        </div>

        {/* Search and Bulk Actions on same line */}
        <div className={styles.searchAndActionsContainer}>
          {/* Search input field */}
          <div className={styles.searchContainer}>
            <SearchInput
              aria-label={searchAriaLabel}
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={handleSearchChange}
              onClear={handleSearchClear}
            />
          </div>

          {/* Bulk Actions */}
          <div className={styles.bulkActions}>
            <Button disabled={paginatedItems.length === 0} onClick={handleSelectAllPage}>
              Select Page
            </Button>
            <Button disabled={selectedItemIds.length === 0} onClick={handleDeselectCurrentPage}>
              Deselect Page
            </Button>
            <Button disabled={filteredItems.length === 0} onClick={handleSelectAll}>
              Select All
            </Button>
            <Button disabled={selectedItemIds.length === 0} onClick={handleSelectNone}>
              Select None
            </Button>
            <span className={styles.selectedCount}>
              {selectedItemIds.length} of {filteredItems.length} selected
            </span>
          </div>
        </div>

        {/* Paginated Item List */}
        <div className={styles.paginatedCheckboxContainer}>
          <div aria-multiselectable="true" className={styles.checkboxListPaginated} role="listbox">
            {paginatedItems.map(item => {
              const isSelected = selectedItemIds.includes(item.id);

              return (
                <div
                  key={item.id}
                  aria-selected={isSelected}
                  className={isSelected ? styles.checkboxItemSelected : styles.checkboxItem}
                  role="option"
                  tabIndex={0}
                  onClick={() => handleItemToggle(item.id)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleItemToggle(item.id);
                    }
                  }}
                >
                  {renderItem(item)}
                </div>
              );
            })}
            {paginatedItems.length === 0 && <div className={styles.emptyState}>{searchTerm ? emptySearchMessage : emptyPageMessage}</div>}
          </div>

          {/* Pagination Footer */}
          <div className={styles.paginationFooter}>
            <div className={styles.paginationInfo}>
              Showing {startIndex + 1}-{endIndex} of {filteredItems.length} items
              {searchTerm && ` (filtered from ${items.length} total)`}
            </div>
            {totalPages > 1 && <Pagination alwaysShow currentPage={currentPage} disabled={false} totalPages={totalPages} onPageChange={setCurrentPage} />}
            {footerActions}
          </div>
        </div>
      </div>
    </div>
  );
}
